import { useCallback, useState } from "react";

import { useField } from "formik";

import { Input } from "components/form/elements";
import { GrabsonIcon } from "components/layout";
import { FieldProps } from "src/types/form";
import { getFieldTestId } from "utils/formik";

import styles from "./CountryInput.scss";
import { useCountryValidator } from "./useCountryValidator";

type CountryInputProps = FieldProps & {
  className?: string;
  disabled?: boolean;
  tooltip?: string;
  // Accept validator as a prop
  validCodes?: Set<string>;
};

export function CountryInput(props: CountryInputProps) {
  const {
    name,
    className,
    required = false,
    disabled = false,
    validCodes: externalValidCodes,
    dataTestId,
  } = props;

  const [field, meta, { setValue }] = useField<string[] | undefined>({
    ...props,
    required,
  });
  const [customValue, setCustomValue] = useState<string | undefined>(undefined);

  const { validCodes } = useCountryValidator(externalValidCodes);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (validCodes.size > 0 && customValue !== undefined) {
      const countries = parseAndValidateCountries(customValue, validCodes);
      setValue(countries).then(() => {
        setCustomValue(undefined);
        field.onBlur(e);
      });
    }
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCustomValue(e.target.value),
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
    []
  );

  return (
    <Input
      {...meta}
      {...props}
      className={className || styles.default}
      disabled={disabled || validCodes.size === 0}
      props={{
        ...field,
        autoComplete: "off",
        value: customValue ?? (meta.value ?? []).join(", "),
        onBlur: handleBlur,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
      }}
      required={required}
      type="text"
      dataTestId={dataTestId || getFieldTestId(name)}
    >
      <GrabsonIcon className={styles.icon} name="edit" size="xl" />
    </Input>
  );
}

function parseAndValidateCountries(
  input: string,
  validCodes: Set<string>
): string[] {
  const countries = input
    .split(",")
    .map((v) => v.trim().toUpperCase())
    .filter((v) => validCodes.has(v));

  // Filter out duplicates
  return countries.filter((c, idx) => countries.indexOf(c) === idx);
}
